import customAxios from "lib/axios";

const APP_DEVICE_ID_KEY = "tradeasy_app_device_id";
const APP_SESSION_ID_KEY = "tradeasy_app_session_id";
const APP_DEVICE_SYNC_PATH =
  process.env.REACT_APP_APP_DEVICE_SYNC_PATH ||
  "/notifications/app/devices/sync";
const APP_FEATURE_USAGE_PATH =
  process.env.REACT_APP_APP_FEATURE_USAGE_PATH ||
  "/notifications/app/feature-usage";
const APP_FEATURE_USAGE_PREFIX = "tradeasy_app_feature_usage_";

const canUseWindow = () => typeof window !== "undefined";

const generateIdentifier = () => {
  if (canUseWindow()) {
    const cryptoApi = window.crypto as Crypto & {
      randomUUID?: () => string;
    };

    if (cryptoApi?.randomUUID) {
      return cryptoApi.randomUUID();
    }
  }

  return `ta-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const isStandaloneMode = () => {
  if (!canUseWindow()) return false;

  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    nav.standalone === true
  );
};

const isBrowserPushCapable = () => {
  if (!canUseWindow()) return false;

  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
};

const isProbablyMobileDevice = () => {
  if (!canUseWindow()) return false;

  const ua = window.navigator.userAgent || "";
  return /android|iphone|ipad|ipod|mobile/i.test(ua);
};

export const getNotificationPermissionStatus = () => {
  if (!canUseWindow() || !("Notification" in window)) {
    return "unsupported";
  }

  return Notification.permission;
};

export const getOrCreateAppDeviceId = () => {
  if (!canUseWindow()) return "";

  const existing = localStorage.getItem(APP_DEVICE_ID_KEY);
  if (existing) return existing;

  const created = generateIdentifier();
  localStorage.setItem(APP_DEVICE_ID_KEY, created);
  return created;
};

export const getOrCreateAppSessionId = () => {
  if (!canUseWindow()) return "";

  const existing = sessionStorage.getItem(APP_SESSION_ID_KEY);
  if (existing) return existing;

  const created = generateIdentifier();
  sessionStorage.setItem(APP_SESSION_ID_KEY, created);
  return created;
};

export const getAppRuntimeMetadata = (route?: string) => {
  return {
    deviceId: getOrCreateAppDeviceId(),
    sessionId: getOrCreateAppSessionId(),
    platform: navigator.platform,
    language: navigator.language,
    userAgent: navigator.userAgent,
    standalone: isStandaloneMode(),
    isMobile: isProbablyMobileDevice(),
    pushSupported: isBrowserPushCapable(),
    notificationPermission: getNotificationPermissionStatus(),
    route:
      route ||
      (canUseWindow() ? window.location.pathname + window.location.search : ""),
  };
};

export const syncAppDeviceState = async (route?: string) => {
  if (!canUseWindow()) return;

  await customAxios.post(APP_DEVICE_SYNC_PATH, getAppRuntimeMetadata(route));
};

export const trackAppFeatureUsage = async (
  featureName: "builder" | "validation",
  route?: string
) => {
  if (!canUseWindow()) return;
  if (!localStorage.getItem("user_id")) return;

  const sessionId = getOrCreateAppSessionId();
  const deviceId = getOrCreateAppDeviceId();
  const dedupeKey = `${APP_FEATURE_USAGE_PREFIX}${sessionId}:${featureName}`;

  if (sessionStorage.getItem(dedupeKey)) {
    return;
  }

  await customAxios.post(APP_FEATURE_USAGE_PATH, {
    ...getAppRuntimeMetadata(route),
    deviceId,
    sessionId,
    featureName,
  });

  sessionStorage.setItem(dedupeKey, "1");
};
